import React, { useEffect, useState } from "react";
import {
  Row, Col, Input, Label,
  Modal, ModalBody, ModalFooter, ModalHeader
} from "reactstrap";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";

import { listProductByParams } from "../../api/product.api";
import {
  createMasterProduct,
  listMasterProductsByParams,
  getSpecsByProduct,
  deleteMasterProduct,
  updateMasterProduct,
  getMasterProductSpecs
} from "../../api/masterProduct.api";

import StatusBadge from "../../components/StatusBadge";

const initialForm = {
  product_id: "",
  brand_name: "",
  model_number: "",
  description: "",
  specs: {},
  is_active: 1
};

const AdminMasterProducts = () => {

  const [form, setForm] = useState(initialForm);
  const [products, setProducts] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [specLoading, setSpecLoading] = useState(false);

  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const [query, setQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(true); 

  /* FETCH PRODUCTS */
  useEffect(() => {
    listProductByParams({ per_page: 100 })
      .then(res => {
        setProducts(res.data.data[0]?.data || []);
      });

    fetchData();
  }, [activeOnly, query]);

  /* FETCH TABLE */
  const fetchData = async () => {
    setLoading(true);

    const res = await listMasterProductsByParams({
      match: query,
      is_active: activeOnly ? 1 : 0
    });

    setData(res.data.data[0]?.data || []);
    setLoading(false);
  };

  /* PRODUCT CHANGE */
  const handleProductChange = async (e) => {
    const product_id = e.target.value;

    setForm(prev => ({
      ...prev,
      product_id,
      specs: {}
    }));

    if (!product_id) return;

    setSpecLoading(true);

    try {
      const res = await getSpecsByProduct(product_id);
      setSpecs(res.data.data || []);
    } catch {
      toast.error("Failed to load specs");
    }

    setSpecLoading(false);
  };

  /* SPEC CHANGE */
  const handleSpecChange = (spec_type_id, value) => {
    setForm(prev => ({
      ...prev,
      specs: {
        ...prev.specs,
        [spec_type_id]: value
      }
    }));
  };

  /* INPUT CHANGE */
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  /* SUBMIT */
  const handleSubmit = async () => {

    const payload = {
      product_id: form.product_id,
      brand_name: form.brand_name,
      model_number: form.model_number,
      description: form.description,
      is_active: form.is_active,
      specs: Object.entries(form.specs).map(([k, v]) => ({
        spec_type_id: k,
        spec_value_id: v   // already exact value
      }))
    };

    try {

      if (modalEdit) {
        await updateMasterProduct(editId, payload);
        toast.success("Updated");
        setModalEdit(false);
      } else {
        await createMasterProduct(payload);
        toast.success("Created");
        setModalAdd(false);
      }

      setForm(initialForm);
      setSpecs([]);
      fetchData();

    } catch {
      toast.error("Error");
    }
  };

  /* EDIT */
  const handleEdit = async (row) => {
    setEditId(row.id);
    setModalEdit(true);

    // 1. load spec structure
    const res1 = await getSpecsByProduct(row.product_id);
    const specsData = res1.data.data || [];

    // 2. load saved values
    const res2 = await getMasterProductSpecs(row.id);

    const specMap = {};
    res2.data.data.forEach(s => {
      specMap[s.spec_id] = s.exact_value;
    });

    // 3. set everything together
    setSpecs(specsData);

    setForm({
      product_id: row.product_id,
      brand_name: row.brand_name,
      model_number: row.model_number,
      description: row.description,
      is_active: row.is_active,
      specs: specMap
    });
  };

  /* DELETE */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;

    try {
      await deleteMasterProduct(id);
      toast.success("Deleted");
      fetchData();
    } catch {
      toast.error("Error deleting");
    }
  };

  /* TABLE STYLE */
  const customStyles = {
    headRow: { style: { borderBottom: "2px solid #f3f4f6" } },
    headCells: {
      style: {
        color: "#6b7280",
        fontWeight: "700",
        textTransform: "uppercase",
        fontSize: "11px",
        letterSpacing: "0.5px"
      }
    },
    cells: { style: { fontSize: "14px", color: "#374151", padding: "15px 10px" } },
  };

  /* TABLE */
  const columns = [
    { name: "Sr No", selector: (r, i) => i + 1, width: "70px" },
    { name: "Brand", selector: r => r.brand_name, style: { fontWeight: "600" } },
    { name: "Model", selector: r => r.model_number },
    {
      name: "Status",
      center: true,
      cell: (r) => (
        <StatusBadge status={r.is_active ? "ACTIVE" : "INACTIVE"} />
      )
    },
    {
      name: "Actions",
      right: true,
      cell: (row) => (
        <div className="d-flex gap-3">
          <button className="btn btn-link p-0 text-muted" onClick={() => handleEdit(row)}>
            🖊️
          </button>
          <button className="btn btn-link p-0 text-danger" onClick={() => handleDelete(row.id)}>
            🗑️
          </button>
        </div>
      )
    }
  ];

  return (
    <div style={{
      backgroundColor: "#fff",
      padding: "clamp(15px, 3vw, 24px)",
      borderRadius: "12px",
      border: "1px solid #e5e7eb"
    }}>

      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div>
          <h4 className="fw-bold m-0" style={{ color: "#112347" }}>
            Master Products
          </h4>
          <p className="text-muted small m-0">Create master products</p>
        </div>

        <button 
          className="btn btn-dark fw-bold px-4 py-2" 
          style={{ backgroundColor: "#112347", borderRadius: "8px", border: "none" }}
          onClick={() => {
            setForm(initialForm);
            setModalAdd(true);
          }}
        >
          + Add Master Product
        </button>
      </div>

      {/* FILTER */}
      <Row className="mb-4 g-2">
        <Col xs="12" md="4" className="position-relative">
          <span className="position-absolute translate-middle-y top-50 ms-3 text-muted">🔍</span>
          <Input
            placeholder="Search Master Products..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", paddingLeft: "40px" }}
          />
        </Col>

        <Col xs="12" md="3">
          <div className="h-100 d-flex align-items-center border rounded px-3"
            style={{ backgroundColor: "#f9fafb", borderColor: "#e5e7eb", minHeight: "40px" }}>
            <Input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            <Label className="ms-2 mb-0 small fw-bold text-muted">Show Active Only</Label>
          </div>
        </Col>
      </Row>

      {/* TABLE */}
      <div className="table-responsive border rounded-3 overflow-hidden">
        <DataTable 
          columns={columns} 
          data={data} 
          progressPending={loading} 
          pagination 
          customStyles={customStyles}
        />
      </div>

      {/* MODAL */}
      <Modal
        isOpen={modalAdd || modalEdit}
        toggle={() => {
          setModalAdd(false);
          setModalEdit(false);
        }}
        backdrop={true}
        size="xl"
        centered
      >
        <ModalHeader className="border-0 pb-0" toggle={() => {
          setModalAdd(false);
          setModalEdit(false);
        }}>
          <span className="fw-bold" style={{ color: "#112347" }}>
            {modalEdit ? "Edit Master Product" : "Add Master Product"}
          </span>
        </ModalHeader>

        <ModalBody className="pt-2">
          <Row className="g-3">
            <Col md="4">
              <Label className="small fw-bold text-muted">Product Category *</Label>
              <Input type="select" value={form.product_id || ""} onChange={handleProductChange} style={{ borderRadius: "8px" }}>
                <option value="">Select</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.product_name}</option>
                ))}
              </Input>
            </Col>

            <Col md="4">
              <Label className="small fw-bold text-muted">Brand Name *</Label>
              <Input name="brand_name" value={form.brand_name} onChange={handleChange} style={{ borderRadius: "8px" }} />
            </Col>

            <Col md="4">
              <Label className="small fw-bold text-muted">Model Number *</Label>
              <Input name="model_number" value={form.model_number} onChange={handleChange} style={{ borderRadius: "8px" }} />
            </Col>

            <Col md="12">
              <Label className="small fw-bold text-muted">Description</Label>
              <Input type="textarea" name="description" value={form.description} onChange={handleChange} style={{ borderRadius: "8px", minHeight: "80px" }} />
            </Col>

            <Col md="4">
              <Label className="small fw-bold text-muted">Configuration Status</Label>
              <div className="d-flex align-items-center mt-2">
                <Input
                  type="checkbox"
                  checked={Number(form.is_active) === 1}
                  onChange={(e) =>
                    setForm({ ...form, is_active: e.target.checked ? 1 : 0 })
                  }
                />
                <Label className="ms-2 mb-0 small fw-bold">
                  {Number(form.is_active) === 1 ? "Active Catalog Item" : "Inactive Catalog Item"}
                </Label>
              </div>
            </Col>
          </Row>

          {/* SPECS SECTION */}
          {specs.length > 0 && (
            <div className="mt-4 pt-3 border-top">
              <h6 className="fw-bold mb-3" style={{ color: "#112347" }}>Technical Attributes</h6>
              <Row className="g-3">
                {specs.map(spec => (
                  <Col md="4" key={spec.spec_type_id}>
                    <Label className="small fw-bold text-muted">{spec.spec_label}</Label>
                    <Input
                      type="select"
                      value={form.specs[spec.spec_type_id] || ""}
                      onChange={(e) =>
                        handleSpecChange(spec.spec_type_id, e.target.value)
                      }
                      style={{ borderRadius: "8px" }}
                    >
                      <option value="">Select Value</option>
                      {spec.values.map(v => (
                        <option key={v.spec_value_id} value={v.value}>
                          {v.value}
                        </option>
                      ))}
                    </Input>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </ModalBody>

        <ModalFooter className="border-0">
          <button 
            className="btn btn-dark w-100 py-2 fw-bold shadow-sm" 
            onClick={handleSubmit}
            style={{ backgroundColor: "#112347", borderRadius: "8px" }}
          >
            {modalEdit ? "Update Master Product" : "Create Master Product"}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AdminMasterProducts;